import { Button, Heading, Icon, Tag, Text } from "@ttc3k/trekker";
import { ArrowLeft, ArrowRight, Calendar } from "iconoir-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { es, fr } from "date-fns/locale";
import { format } from "date-fns";
import { Box, Flex, Grid } from "styled-system/jsx";
import { EnumPromotionAvailabilityType, type AssetImage } from "gql/generated";
import { SmartImage } from "shared/components/atoms";
import { HtmlContent } from "shared/components/molecules/HtmlContent";
import {
  formatPromotionDates,
  listPromotionDates,
  parsePromotionDate,
  type PromotionAvailabilityLike,
} from "shared/utils/promotionHelpers";

export type PromotionDetailsData = {
  title: string;
  description: string;
  link?: string | null;
  availability: PromotionAvailabilityLike;
  images: Partial<AssetImage>[];
  listingName?: string;
};

type PromotionDetailsProps = {
  promotion: PromotionDetailsData;
  /** When provided, a "Learn more about <listing>" link is shown. */
  onListingClick?: () => void;
  /** Width of the hero image container in px (defaults to the 520px pane). */
  heroWidth?: number;
};

/**
 * Promotion details view: hero gallery, listing, title, link, dates and
 * description. Used by the in-app preview and (later) the widget slideouts.
 */
export const PromotionDetails = ({
  promotion,
  onListingClick,
  heroWidth = 520,
}: PromotionDetailsProps) => {
  const { t, i18n } = useTranslation("events", {
    keyPrefix: "APP.PROMOTION_MODAL.PREVIEW",
  });
  const locale =
    i18n.language === "fr" ? fr : i18n.language === "es" ? es : undefined;
  const [index, setIndex] = useState(0);
  const images = promotion.images;
  const image = images[index] ?? images[0];
  const heroHeight = Math.round((heroWidth * 368) / 520);

  const isSpecificDates =
    promotion.availability.type === EnumPromotionAvailabilityType.SpecificDates;
  const dateLabels = useMemo(
    () => ({
      ongoing: t("ONGOING"),
      more: (count: number) => t("MORE", { count }),
    }),
    [t],
  );
  // Pill: up to three dates, then "+ x more"; a range or the ongoing label otherwise
  const dateSummary = useMemo(
    () => formatPromotionDates(promotion.availability, dateLabels, locale, 3),
    [promotion.availability, dateLabels, locale],
  );
  const dateChips = useMemo(() => {
    const { availability } = promotion;
    if (availability.type === EnumPromotionAvailabilityType.SpecificDates) {
      return listPromotionDates(availability).map(date =>
        format(parsePromotionDate(date), "EEE, MMM d", { locale }),
      );
    }
    return [formatPromotionDates(availability, dateLabels, locale)];
  }, [promotion, locale, dateLabels]);

  return (
    <Flex flexDir="column" bg="ink.200">
      {/* Hero */}
      <Box
        position="relative"
        w="full"
        h={`${heroHeight}px`}
        bg="ink.200"
        overflow="hidden"
      >
        {image && (
          <SmartImage
            image={image}
            width={heroWidth}
            height={heroHeight}
            shape="square"
            smartFill
          />
        )}
        {images.length > 1 && (
          <>
            <Text
              as="span"
              position="absolute"
              left="16px"
              bottom="16px"
              px="100"
              py="25"
              borderRadius="50"
              bg="ink.1000/75"
              color="white"
              fontSize="3xs"
              lineHeight="2xs"
              fontWeight="medium"
            >
              {index + 1}/{images.length}
            </Text>
            <Flex position="absolute" right="16px" bottom="16px" gap="100">
              <Button
                visual="unstyled"
                type="button"
                w="36px"
                h="36px"
                p="0"
                borderRadius="75"
                bg="ink.1000/75"
                display="flex"
                alignItems="center"
                justifyContent="center"
                onClick={() =>
                  setIndex(prev => (prev - 1 + images.length) % images.length)
                }
              >
                <Icon
                  Element={ArrowLeft}
                  color="icon.inverse"
                  width="18px"
                  height="18px"
                />
              </Button>
              <Button
                visual="unstyled"
                type="button"
                w="36px"
                h="36px"
                p="0"
                borderRadius="75"
                bg="ink.1000/75"
                display="flex"
                alignItems="center"
                justifyContent="center"
                onClick={() => setIndex(prev => (prev + 1) % images.length)}
              >
                <Icon
                  Element={ArrowRight}
                  color="icon.inverse"
                  width="18px"
                  height="18px"
                />
              </Button>
            </Flex>
          </>
        )}
      </Box>

      {/* Layout per Promos > flyout (90:5232): 40px side padding, 24px rhythm */}
      <Flex flexDir="column" gap="300" px="500" pt="600" pb="600">
        {/* Tag + date pill */}
        <Flex justifyContent="space-between" alignItems="center" gap="250">
          <Tag visual="accent.purple" flexShrink={0}>
            {t("BADGE")}
          </Tag>
          <Flex
            alignItems="center"
            gap="100"
            px="150"
            py="50"
            bg="#E3E3E6"
            borderRadius="full"
            minW={0}
          >
            <Icon
              Element={Calendar}
              width="20px"
              height="20px"
              color="icon.light"
              flexShrink={0}
            />
            <Text visual="bodySemiBold" color="text.mid" truncate>
              {dateSummary}
            </Text>
          </Flex>
        </Flex>

        {/* Title + listing */}
        <Flex flexDir="column" gap="150">
          <Heading
            size="h4"
            w="full"
            maxW="none"
            textWrap="wrap"
            fontSize="32px"
            lineHeight="40px"
          >
            {promotion.title}
          </Heading>
          {promotion.listingName && (
            <Text visual="bodySemiBold" color="text.light">
              {promotion.listingName}
            </Text>
          )}
        </Flex>

        {promotion.link && (
          <Button
            visual="primary"
            mode="light"
            type="button"
            width="full"
            gap="100"
            onClick={() =>
              window.open(promotion.link ?? "", "_blank", "noopener")
            }
          >
            {t("MORE_DETAILS")}
            <Icon Element={ArrowRight} width="20px" height="20px" />
          </Button>
        )}

        <HtmlContent html={promotion.description} />

        {/* Dates */}
        <Flex flexDir="column" gap="100">
          <Text visual="smallSemiBold" color="text.light">
            {t("DATES")}
          </Text>
          <Grid
            gridTemplateColumns={
              isSpecificDates ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)"
            }
            gap="50"
          >
            {dateChips.map((chip, i) => (
              <Flex
                key={`${chip}-${i}`}
                alignItems="center"
                h="40px"
                px="200"
                py="100"
                bg="#E3E3E6"
                borderRadius="75"
              >
                <Text visual="bodyMedium" color="text.dark">
                  {chip}
                </Text>
              </Flex>
            ))}
          </Grid>
        </Flex>

        {onListingClick && promotion.listingName && (
          <Button
            visual="unstyled"
            type="button"
            width="fit-content"
            p="0"
            gap="50"
            color={{
              base: "text.accent.blue.mid",
              hover: "text.accent.blue.dark",
              focusVisible: "text.accent.blue.dark",
            }}
            fontSize="md"
            lineHeight="sm"
            fontWeight="medium"
            onClick={onListingClick}
          >
            {t("LEARN_MORE", { listing: promotion.listingName })}
            <Icon Element={ArrowRight} width="20px" height="20px" />
          </Button>
        )}
      </Flex>
    </Flex>
  );
};
